'use client'

import styled from '@emotion/styled'
import AutoGraphIcon from '@mui/icons-material/AutoGraph'
import BarChartIcon from '@mui/icons-material/BarChart'
import BoltIcon from '@mui/icons-material/Bolt'
import GroupIcon from '@mui/icons-material/Group'
import LayersIcon from '@mui/icons-material/Layers'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import PsychologyIcon from '@mui/icons-material/Psychology'
import RefreshIcon from '@mui/icons-material/Refresh'
import StorageIcon from '@mui/icons-material/Storage'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { motion } from 'framer-motion'
import Image from 'next/image'

const Section = styled.section`
  padding: 96px 0;
  background: linear-gradient(180deg, #ffffff 0%, #fafafa 50%, #ffffff 100%);
  position: relative;
  overflow: hidden;
`

const GridPattern = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(to right, rgba(128, 128, 128, 0.03) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(128, 128, 128, 0.03) 1px, transparent 1px);
  background-size: 4rem 4rem;
  pointer-events: none;
`

const Badge = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(39, 177, 198, 0.1);
  border: 1px solid rgba(39, 177, 198, 0.2);
  border-radius: 9999px;
  padding: 6px 20px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0e7490;
  margin-bottom: 24px;
`

const IconBox = styled(Box)`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(39, 177, 198, 0.2),
    rgba(39, 177, 198, 0.1)
  );
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  transition: transform 0.3s;
`

const FeatureCard = styled(Card)`
  height: 100%;
  background: #fafafa;
  background-image: none;
  border: none;
  border-top: 4px solid #0e7490;
  border-radius: 12px;
  box-shadow:
    0 1px 2px -1px rgba(0, 0, 0, 0.1),
    0 4px 6px -1px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;

  &:hover {
    box-shadow:
      0 1px 2px -1px rgba(0, 0, 0, 0.1),
      0 8px 10px -1px rgba(0, 0, 0, 0.12);
    transform: translateY(-4px);

    .icon-box {
      transform: scale(1.1);
    }
  }
`

const CapabilityCard = styled(Card)`
  height: 100%;
  background: #fafafa;
  background-image: none;
  border: none;
  border-radius: 12px;
  box-shadow:
    0 1px 2px -1px rgba(0, 0, 0, 0.1),
    0 8px 10px -1px rgba(0, 0, 0, 0.12);
`

const coreFeatures = [
  {
    icon: <LayersIcon sx={{ fontSize: 32, color: '#0e7490' }} />,
    title: 'Multi-Timeframe Analysis',
    description:
      'Sophisticated CNN architecture processing minute, hourly, and daily market data simultaneously to capture patterns across temporal scales.',
  },
  {
    icon: <StorageIcon sx={{ fontSize: 32, color: '#0e7490' }} />,
    title: 'Comprehensive Data Integration',
    description:
      'Combines market data, on-chain analytics, orderbook dynamics, and sentiment analysis for holistic market understanding.',
  },
  {
    icon: <BoltIcon sx={{ fontSize: 32, color: '#0e7490' }} />,
    title: 'High-Frequency Execution',
    description:
      'Sub-50ms prediction latency enables rapid decision-making with complete trade execution in 100-300ms including network overhead.',
  },
  {
    icon: <AutoGraphIcon sx={{ fontSize: 32, color: '#0e7490' }} />,
    title: 'Proven Performance',
    description:
      'Consistently achieves 1.15 profit factor through systematic exploitation of cross-timeframe relationships and microstructural patterns.',
  },
]

const aiCapabilities = [
  {
    icon: <PsychologyIcon sx={{ fontSize: 28, color: '#0e7490' }} />,
    title: 'Proprietary Models',
    description:
      'Our exclusive AI models are designed for financial markets, blending extensive investment expertise with state-of-the-art data science.',
  },
  {
    icon: <RefreshIcon sx={{ fontSize: 28, color: '#0e7490' }} />,
    title: 'Continuous Adaptation',
    description:
      'The Polar One AI engine evolves through self-learning and feedback, adapting to market dynamics while upholding strict risk management.',
  },
  {
    icon: <GroupIcon sx={{ fontSize: 28, color: '#0e7490' }} />,
    title: 'Human Expertise',
    description:
      'AI powers our analysis, while expert judgment, context, and customized strategies are applied throughout the process.',
  },
]

const strategicApps = [
  {
    icon: <LightbulbIcon sx={{ fontSize: 28, color: '#0e7490' }} />,
    title: 'LLM-Powered Insights',
    description:
      'Utilizing Large Language Models for advanced sentiment analysis and identification of emerging macroeconomic trends.',
  },
  {
    icon: <BoltIcon sx={{ fontSize: 28, color: '#0e7490' }} />,
    title: 'Liquidity-Driven Execution',
    description:
      'Employing fast execution models that dynamically adapt to market liquidity for optimal trade performance.',
  },
  {
    icon: <BarChartIcon sx={{ fontSize: 28, color: '#0e7490' }} />,
    title: 'Pattern Recognition',
    description:
      'Leveraging history-aware models to detect and interpret automated trading patterns and order flows.',
  },
]

function CapabilityItem({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode
  title: string
  description: string
}) {
  return (
    <Box sx={{ display: 'flex', gap: 2.5, mb: 4 }}>
      <Box
        sx={{
          flexShrink: 0,
          width: 56,
          height: 56,
          borderRadius: 3,
          background:
            'linear-gradient(135deg, rgba(39,177,198,0.2), rgba(39,177,198,0.1))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          variant='subtitle1'
          sx={{ fontWeight: 700, mb: 0.5, color: '#171717' }}
        >
          {title}
        </Typography>
        <Typography sx={{ color: '#6b7280', lineHeight: 1.7 }}>
          {description}
        </Typography>
      </Box>
    </Box>
  )
}

export default function AITechnologySection() {
  return (
    <Section>
      <GridPattern />
      <Container maxWidth='lg' sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 10 }}>
          <Badge>Technology</Badge>
          <Typography variant='h3' sx={{ fontWeight: 700, mb: 2, color: '#171717' }}>
            Advanced AI Trading Technology
          </Typography>
          <Typography
            sx={{
              color: '#6b7280',
              fontSize: '1.125rem',
              maxWidth: 700,
              mx: 'auto',
            }}
          >
            Research-backed neural network architecture designed for cryptocurrency
            market excellence
          </Typography>
        </Box>

        {/* Core Features */}
        <Grid container spacing={3} sx={{ mb: 12 }}>
          {coreFeatures.map((f, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, lg: 3 }}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                style={{ height: '100%' }}
              >
                <FeatureCard elevation={0}>
                  <CardContent sx={{ p: 4 }}>
                    <IconBox className='icon-box'>{f.icon}</IconBox>
                    <Typography
                      variant='h6'
                      sx={{ fontWeight: 700, mb: 1.5, color: '#171717' }}
                    >
                      {f.title}
                    </Typography>
                    <Typography sx={{ color: '#6b7280', lineHeight: 1.7 }}>
                      {f.description}
                    </Typography>
                  </CardContent>
                </FeatureCard>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Dashboard Image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{
            marginBottom: 96,
            maxWidth: 900,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            style={{ position: 'relative' }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(135deg, rgba(14,116,144,0.2), rgba(14,116,144,0.05))',
                borderRadius: '16px',
                filter: 'blur(32px)',
              }}
            />
            <Image
              src='/assets/hero/dashboard.webp'
              alt='Polar Tensor Trading Dashboard'
              width={1200}
              height={700}
              style={{
                position: 'relative',
                width: '100%',
                height: 'auto',
                borderRadius: 16,
                boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
                border: '1px solid rgba(14,116,144,0.2)',
              }}
            />
          </motion.div>
        </motion.div>

        {/* Investment Engine */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant='h3' sx={{ fontWeight: 700, mb: 2, color: '#171717' }}>
            Our Intelligent Investment Engine
          </Typography>
          <Typography
            sx={{
              color: '#6b7280',
              fontSize: '1.125rem',
              maxWidth: 700,
              mx: 'auto',
            }}
          >
            Discover how our AI-driven strategies analyze markets, optimize
            portfolios, and manage risk for maximum investment outcomes
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              style={{ height: '100%' }}
            >
              <CapabilityCard elevation={0}>
                <CardContent sx={{ p: { xs: 4, lg: 5 } }}>
                  <Typography
                    variant='h5'
                    sx={{ fontWeight: 700, mb: 4, color: '#171717' }}
                  >
                    Core AI Capabilities
                  </Typography>
                  {aiCapabilities.map((c, i) => (
                    <CapabilityItem key={i} {...c} />
                  ))}
                </CardContent>
              </CapabilityCard>
            </motion.div>
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              style={{ height: '100%' }}
            >
              <CapabilityCard
                elevation={0}
                sx={{
                  background:
                    'linear-gradient(135deg, rgba(39,177,198,0.05), rgba(39,177,198,0.1)) !important',
                  border: '1px solid rgba(39,177,198,0.2)',
                }}
              >
                <CardContent sx={{ p: { xs: 4, lg: 5 } }}>
                  <Typography
                    variant='h5'
                    sx={{ fontWeight: 700, mb: 4, color: '#171717' }}
                  >
                    Strategic AI Applications
                  </Typography>
                  {strategicApps.map((c, i) => (
                    <CapabilityItem key={i} {...c} />
                  ))}
                </CardContent>
              </CapabilityCard>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Section>
  )
}
